import { Combobox, ComboboxOption } from '@schuettflix/react-components';
import { useTranslation } from 'react-i18next';
import { LocationType } from '@/modules/location/domain/LocationSchema.ts';
import { z } from 'zod';
import { format, GeoAddressSchema } from '@/modules/location/domain/GeoAddressSchema.ts';

const locationTitleUnloading = 'location.locationSelection.titleUnloading';
const locationTitleLoading = 'location.locationSelection.titleLoading';
const locationPlaceholder = 'location.locationSelection.locationPlaceholder';

export const FormLocationSelection = z.object({
    id: z.string().uuid(),
    name: z.string(),
    geoAddress: GeoAddressSchema,
    isCustom: z.boolean(),
    organizationId: z.number().optional(),
    legacyFactoryId: z.number().nullish(),
});

export type FormLocationSelection = z.infer<typeof FormLocationSelection>;

interface LocationSelectionProps {
    locationType: LocationType;
    value?: FormLocationSelection | null;
    errorMessage?: string;
    onChange: (value: FormLocationSelection | null) => void;
    locations: FormLocationSelection[];
}

export const LocationSelection: React.FC<LocationSelectionProps> = ({
    locationType,
    locations,
    value,
    onChange,
    errorMessage,
}) => {
    const { t } = useTranslation();
    const title = locationType === 'LOADING' ? t(locationTitleLoading) : t(locationTitleUnloading);

    const handleOnChange = (newValue: string | null) => {
        const selected = locations.find(location => location.id === newValue);
        if (selected && selected.id !== value?.id) {
            onChange(selected);
        }
    };

    const getOptions = () => {
        return locations.map(
            (location): ComboboxOption<string> => ({
                label: !location.name ? t('location.customName') : location.name,
                value: location.id,
                description: format(location.geoAddress),
            })
        );
    };

    return (
        <div className="flex flex-col gap-4">
            <div>
                <h3 className="font-copy-md-strong mb-2">{title}</h3>
                {locations && (
                    <Combobox
                        className={errorMessage ? 'mb-4' : ''}
                        value={value?.id || null}
                        onChange={handleOnChange}
                        renderSelection={value => {
                            return value.selected?.description;
                        }}
                        errorMessage={errorMessage}
                        placeholder={t(locationPlaceholder)}
                        options={getOptions()}
                        multiple={false}
                        variant="sm"
                        dataTest="location-input"
                    />
                )}
            </div>
        </div>
    );
};
